import { useState, useEffect } from 'react';
import { Modal } from 'flowbite-react';
import ReportsAccordion from './ReportsAccordion';

// Small, subtle NoData component
const NoData = () => (
  <div className="text-left py-0 text-sm text-gray-500 dark:text-gray-400">
    No Data
  </div>
);

const DietRequestDetails = ({ modalData, showModal, onClose, getCountryName, isAdmin, countries, envName }) => {
  const [expandedSections, setExpandedSections] = useState(new Set());
  const [showEmptyData, setShowEmptyData] = useState(false);

  // Reset expanded sections and empty data toggle when modal opens
  useEffect(() => {
    if (showModal) {
      setExpandedSections(new Set());
      setShowEmptyData(false);
    }
  }, [showModal]);

  // Early return if no modal data or not showing
  if (!showModal || !modalData) return null;

  // Toggle accordion sections
  const toggleSection = (section) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(section)) {
      newExpanded.delete(section);
    } else {
      newExpanded.add(section);
    }
    setExpandedSections(newExpanded);
  };

  const recommendation = modalData?.recommendation;

  // Get analysis URL
  const getAnalysisUrl = () => {
    if (!modalData) return null;

    const analysisUrl = modalData?.analysisUrl;
    const urlPrefix = modalData?.siteBaseUrlPrefix;
    const urlSuffix = modalData?.siteBaseUrlSuffix;

    if (analysisUrl) {
      if (urlPrefix && urlSuffix) {
        const envPart = (envName && envName !== 'prod') ? `-${envName}` : '';
        return `${urlPrefix}${envPart}${urlSuffix}?${analysisUrl}`;
      }
    }

    return null;
  };

  const analysisUrl = getAnalysisUrl();

  return (
    <Modal show={showModal} onClose={onClose} size="7xl">
      <Modal.Header className="relative bg-gray-200 dark:bg-gray-700 border-b border-gray-300 dark:border-gray-600 [&>h3]:mr-[6rem] ">
        <div className="flex items-center justify-between w-full h-full">
          <div className="text-gray-900 dark:text-white font-semibold flex items-center">
            {recommendation?.horse?.name ?
              `${recommendation.horse.name}'s diet request in detail` :
              'Diet request in detail'
            }
          </div>
        </div>
        <div className="flex absolute top-1/2 -translate-y-1/2 right-16 items-center space-x-2">
          <span className="text-sm text-gray-500 dark:text-gray-400 hidden sm:block">Compact</span>
          <div className="flex flex-col items-center">
            <button
              onClick={() => setShowEmptyData(!showEmptyData)}
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none ${
                !showEmptyData
                  ? 'bg-blue-600 dark:bg-blue-500'
                  : 'bg-gray-300 dark:bg-gray-600'
              }`}
            >
              <span
                className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                  !showEmptyData ? 'translate-x-5' : 'translate-x-1'
                }`}
              />
            </button>
            <span className="text-xs text-gray-500 dark:text-gray-400 sm:hidden mt-1">Compact</span>
          </div>
        </div>
      </Modal.Header>
      <Modal.Body className="p-0">
        <div className="grid grid-cols-1 sm:grid-cols-2 min-h-[600px]">
          {/* Left Column - Input and Analytics (1/2) */}
          <div className="p-6 sm:border-r border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 overflow-y-auto">
            <div className="mb-4">
              {/* Basic Info */}
              {isAdmin && (
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-600 dark:text-gray-400">ID</span>
                  <span className="font-medium text-gray-900 dark:text-white text-right break-words">{recommendation?.recommendationId || '-'}</span>
                </div>
              )}
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-600 dark:text-gray-400">Date</span>
                <span className="font-medium text-gray-900 dark:text-white text-right break-words">{recommendation?.recommendationDate ? new Date(recommendation.recommendationDate).toLocaleDateString('en-GB', {
                  day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit'
                }) : '-'}</span>
              </div>
              {countries && countries.length > 1 && (
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-600 dark:text-gray-400">Country</span>
                  <span className="font-medium text-gray-900 dark:text-white text-right break-words">{getCountryName(recommendation?.country)}</span>
                </div>
              )}
              {(() => {
                const userForm = recommendation?.userForm;
                const hasUserData = userForm && (
                  (userForm.firstName?.trim()) ||
                  (userForm.lastName?.trim()) ||
                  (userForm.email?.trim())
                );

                return (showEmptyData || hasUserData) && (
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-600 dark:text-gray-400">User</span>
                    <span className="font-medium text-gray-900 dark:text-white text-right break-words">
                      {(() => {
                        if (!userForm) return '-';
                        const parts = [];
                        if (userForm.firstName?.trim()) parts.push(userForm.firstName.trim());
                        if (userForm.lastName?.trim()) parts.push(userForm.lastName.trim());
                        const name = parts.join(' ');
                        const email = userForm.email?.trim();

                        if (name && email) {
                          return (
                            <>
                              {name} (
                              <a
                                href={`mailto:${email}`}
                                className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 underline underline-offset-4"
                                title={`Send email to ${email}`}
                              >
                                {email}
                              </a>
                              )
                            </>
                          );
                        } else if (email) {
                          return (
                            <a
                              href={`mailto:${email}`}
                              className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 underline underline-offset-4"
                              title={`Send email to ${email}`}
                            >
                              {email}
                            </a>
                          );
                        } else if (name) {
                          return name;
                        }
                        return '-';
                      })()}
                    </span>
                  </div>
                );
              })()}
              {isAdmin && (
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-600 dark:text-gray-400">Client</span>
                  <span className="font-medium text-gray-900 dark:text-white text-right break-words">{recommendation?.profileName || '-'}</span>
                </div>
              )}
            </div>

            <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">Input</h2>

            {/* Horse Section */}
            <div className="mb-4">
              <button
                className="w-full flex items-center justify-between p-3 bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 rounded-lg transition-colors"
                onClick={() => toggleSection('horse')}
              >
                <span className="font-medium text-gray-900 dark:text-white">Horse</span>
                <svg
                  className={`w-4 h-4 transition-transform text-gray-600 dark:text-gray-300 ${expandedSections.has('horse') ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {expandedSections.has('horse') && (
                <div className="mt-2 bg-white dark:bg-gray-700 p-4 rounded-lg">
                  <div className="space-y-2 text-sm text-gray-800 dark:text-gray-200">
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Horse Name</span>
                      <span className="font-medium text-right break-words">{recommendation?.horse?.name || '-'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Growing</span>
                      <span className="font-medium text-right break-words">{recommendation?.horse?.growing === true ? 'Yes' : recommendation?.horse?.growing === false ? 'No' : '-'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Birth Year</span>
                      <span className="font-medium text-right break-words">{recommendation?.horse?.birthYear || '-'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Birth Month</span>
                      <span className="font-medium text-right break-words">{recommendation?.horse?.birthMonth || '-'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Breed Type</span>
                      <span className="font-medium text-right break-words">{recommendation?.horse?.breedType || '-'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Sex</span>
                      <span className="font-medium text-right break-words">{recommendation?.horse?.sex || '-'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Weight</span>
                      <span className="font-medium text-right break-words">{(() => {
                        const horse = recommendation?.horse;
                        if (!horse) return '-';
                        const weight = horse.weight;
                        const unit = horse.weightUnit || 'kg';
                        if (weight !== undefined && weight !== null) {
                          const formattedWeight = typeof weight === 'number' ?
                            Number(weight).toFixed(2) : weight;
                          return `${formattedWeight} ${unit}`;
                        }
                        return '-';
                      })()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Current activity</span>
                      <span className="font-medium text-right break-words">{recommendation?.horse?.activityLevel || '-'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Horse Discipline</span>
                      <span className="font-medium text-right break-words">{recommendation?.horse?.horseDiscipline || '-'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Best Described As</span>
                      <span className="font-medium text-right break-words">{recommendation?.horse?.keeperQuality || '-'}</span>
                    </div>
                    {(() => {
                      const goals = recommendation?.horse?.goals;
                      const hasGoals = Array.isArray(goals) && goals.length > 0;
                      return (showEmptyData || hasGoals) && (
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">Goals</span>
                          <span className="font-medium text-right break-words">
                            {hasGoals ? goals.map(goal => goal.name || goal).join(', ') : '-'}
                          </span>
                        </div>
                      );
                    })()}
                    {(() => {
                      const problems = recommendation?.horse?.problems;
                      const hasProblems = Array.isArray(problems) && problems.length > 0;
                      return (showEmptyData || hasProblems) && (
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">Problems</span>
                          <span className="font-medium text-right break-words">
                            {hasProblems ? problems.map(problem => problem.name || problem).join(', ') : '-'}
                          </span>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              )}
            </div>

            {/* Pasture Section */}
            {(() => {
              const pasture = recommendation?.pasture;
              const hasPasture = pasture && typeof pasture === 'object';
              if (!showEmptyData && !hasPasture) return null;

              return (
                <div className="mb-4">
                  <button
                    className="w-full flex items-center justify-between p-3 bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 rounded-lg transition-colors"
                    onClick={() => toggleSection('pasture')}
                  >
                    <span className="font-medium text-gray-900 dark:text-white">Pasture</span>
                    <svg
                      className={`w-4 h-4 transition-transform text-gray-600 dark:text-gray-300 ${expandedSections.has('pasture') ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {expandedSections.has('pasture') && (
                    <div className="mt-2 bg-white dark:bg-gray-700 p-4 rounded-lg">
                      {hasPasture ? (
                        <div className="space-y-2 text-sm text-gray-800 dark:text-gray-200">
                          <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-gray-400">Time on pasture</span>
                            <span className="font-medium text-right break-words">{pasture.time ? `${pasture.time} hours` : '-'}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-gray-400">Overgrazed</span>
                            <span className="font-medium text-right break-words">{pasture.overgrazed === true ? 'Yes' : pasture.overgrazed === false ? 'No' : '-'}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-gray-400">Quality</span>
                            <span className="font-medium text-right break-words">{pasture.quality || '-'}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-gray-400">Type</span>
                            <span className="font-medium text-right break-words">{pasture.type || '-'}</span>
                          </div>
                        </div>
                      ) : (
                        <NoData />
                      )}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Forages Section */}
            {(() => {
              const forages = recommendation?.forages;
              const hasForages = forages && Array.isArray(forages) && forages.length > 0 && forages.some(forage => forage && (forage.id || forage.name || forage.quantity));
              if (!showEmptyData && !hasForages) return null;

              return (
                <div className="mb-4">
                  <button
                    className="w-full flex items-center justify-between p-3 bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 rounded-lg transition-colors"
                    onClick={() => toggleSection('forages')}
                  >
                    <span className="font-medium text-gray-900 dark:text-white">Forages</span>
                    <svg
                      className={`w-4 h-4 transition-transform text-gray-600 dark:text-gray-300 ${expandedSections.has('forages') ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {expandedSections.has('forages') && (
                    <div className="mt-2 bg-white dark:bg-gray-700 p-4 rounded-lg">
                      {hasForages ? (
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs border border-gray-200 dark:border-gray-600">
                            <thead>
                              <tr className="bg-gray-50 dark:bg-gray-700">
                                <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-300 border-r border-gray-200 dark:border-gray-600">ID</th>
                                <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-300 border-r border-gray-200 dark:border-gray-600">Name</th>
                                <th className="px-3 py-2 text-right font-medium text-gray-600 dark:text-gray-300">Quantity</th>
                              </tr>
                            </thead>
                            <tbody>
                              {forages.map((forage, forageIndex) => (
                              <tr key={forageIndex} className="border-t border-gray-200 dark:border-gray-600">
                                <td className="px-3 py-2 text-gray-800 dark:text-gray-200 border-r border-gray-200 dark:border-gray-600">
                                  {forage?.id || '-'}
                                </td>
                                <td className="px-3 py-2 text-gray-800 dark:text-gray-200 border-r border-gray-200 dark:border-gray-600">
                                  {(() => {
                                    const name = forage?.name || `Forage ${forageIndex + 1}`;
                                    const quality = forage?.quality;
                                    return quality ? `${name} (${quality})` : name;
                                  })()}
                                </td>
                                <td className="px-3 py-2 text-right text-gray-800 dark:text-gray-200">
                                  {(() => {
                                    const quantity = forage?.quantity;
                                    const unit = forage?.unit;
                                    if (quantity !== undefined && quantity !== null) {
                                      const formattedQuantity = typeof quantity === 'number' ?
                                        Number(quantity).toFixed(2) : quantity;
                                      return unit ? `${formattedQuantity} ${unit}` : String(formattedQuantity);
                                    }
                                    return '-';
                                  })()}
                                </td>
                              </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <NoData />
                      )}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* User Selected Feed Section */}
            {(() => {
              const userSelectedFeedId = recommendation?.userSelectedFeedId;
              const userSelectedFeed = recommendation?.userSelectedFeed;
              const hasUserSelectedFeed = userSelectedFeedId > 0;
              if (!showEmptyData && !hasUserSelectedFeed) return null;

              return (
                <div className="mb-4">
                  <button
                    className="w-full flex items-center justify-between p-3 bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 rounded-lg transition-colors"
                    onClick={() => toggleSection('userSelectedFeed')}
                  >
                    <span className="font-medium text-gray-900 dark:text-white">User Selected Feed</span>
                    <svg
                      className={`w-4 h-4 transition-transform text-gray-600 dark:text-gray-300 ${expandedSections.has('userSelectedFeed') ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {expandedSections.has('userSelectedFeed') && (
                    <div className="mt-2 bg-white dark:bg-gray-700 p-4 rounded-lg">
                      {hasUserSelectedFeed ? (
                        <div className="space-y-2 text-sm text-gray-800 dark:text-gray-200">
                          <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-gray-400">Id</span>
                            <span className="font-medium text-right break-words">{userSelectedFeedId}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-gray-400">Name</span>
                            <span className="font-medium text-right break-words">{userSelectedFeed?.name || '-'}</span>
                          </div>
                        </div>
                      ) : (
                        <NoData />
                      )}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Analytics Section */}
            {isAdmin ? (
              <div className="mb-6">
                <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">Analytics</h2>

                {/* Source Section */}
                <div className="mb-4">
                  <button
                    className="w-full flex items-center justify-between p-3 bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 rounded-lg transition-colors"
                    onClick={() => toggleSection('analytics-source')}
                  >
                    <span className="font-medium text-gray-900 dark:text-white">Source</span>
                    <svg
                      className={`w-4 h-4 transition-transform text-gray-600 dark:text-gray-300 ${expandedSections.has('analytics-source') ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {expandedSections.has('analytics-source') && (
                    <div className="mt-2 bg-white dark:bg-gray-700 p-4 rounded-lg">
                      <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">Host</span>
                          <span className="font-medium text-right break-words">{recommendation?.analyticsData?.host || '-'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">Referrer</span>
                          <span className="font-medium text-right break-all">
                            {(() => {
                              const referrer = recommendation?.analyticsData?.referrer;
                              if (!referrer) return '-';

                              // Check if it's a URL
                              const isUrl = referrer.startsWith('http') || referrer.startsWith('www') || referrer.includes('://');

                              if (isUrl) {
                                return (
                                  <a
                                    href={referrer}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 underline underline-offset-4 break-all"
                                    title={referrer}
                                  >
                                    {referrer}
                                  </a>
                                );
                              }

                              return referrer;
                            })()}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Location Section */}
                <div className="mb-4">
                  <button
                    className="w-full flex items-center justify-between p-3 bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 rounded-lg transition-colors"
                    onClick={() => toggleSection('analytics-location')}
                  >
                    <span className="font-medium text-gray-900 dark:text-white">Location</span>
                    <svg
                      className={`w-4 h-4 transition-transform text-gray-600 dark:text-gray-300 ${expandedSections.has('analytics-location') ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {expandedSections.has('analytics-location') && (
                    <div className="mt-2 bg-white dark:bg-gray-700 p-4 rounded-lg">
                      <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">IP Address</span>
                          <span className="font-medium text-right break-words">{recommendation?.analyticsData?.ipAddress || '-'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">Country</span>
                          <span className="font-medium text-right break-words">{getCountryName(recommendation?.analyticsData?.country) || '-'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">City</span>
                          <span className="font-medium text-right break-words">{recommendation?.analyticsData?.city || '-'}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Client Section */}
                <div className="mb-4">
                  <button
                    className="w-full flex items-center justify-between p-3 bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 rounded-lg transition-colors"
                    onClick={() => toggleSection('analytics-client')}
                  >
                    <span className="font-medium text-gray-900 dark:text-white">Client</span>
                    <svg
                      className={`w-4 h-4 transition-transform text-gray-600 dark:text-gray-300 ${expandedSections.has('analytics-client') ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {expandedSections.has('analytics-client') && (
                    <div className="mt-2 bg-white dark:bg-gray-700 p-4 rounded-lg">
                      <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">Browser</span>
                          <span className="font-medium text-right break-words">{recommendation?.analyticsData?.browser || '-'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">Browser Version</span>
                          <span className="font-medium text-right break-words">{recommendation?.analyticsData?.browserVersion || '-'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">OS</span>
                          <span className="font-medium text-right break-words">{recommendation?.analyticsData?.os || '-'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">OS Version</span>
                          <span className="font-medium text-right break-words">{recommendation?.analyticsData?.osVersion || '-'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">User Agent</span>
                          <span className="font-medium text-right break-all text-xs">{recommendation?.analyticsData?.userAgent || '-'}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* System Info Section */}
                <div className="mb-4">
                  <button
                    className="w-full flex items-center justify-between p-3 bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 rounded-lg transition-colors"
                    onClick={() => toggleSection('analytics-system')}
                  >
                    <span className="font-medium text-gray-900 dark:text-white">System Info</span>
                    <svg
                      className={`w-4 h-4 transition-transform text-gray-600 dark:text-gray-300 ${expandedSections.has('analytics-system') ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {expandedSections.has('analytics-system') && (
                    <div className="mt-2 bg-white dark:bg-gray-700 p-4 rounded-lg">
                      <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">Product Matrix Version</span>
                          <span className="font-medium text-right break-words">{recommendation?.productMatrixVersion || '-'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">Settings Version</span>
                          <span className="font-medium text-right break-words">{recommendation?.settingsVersion || '-'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">System Version</span>
                          <span className="font-medium text-right break-words">{recommendation?.systemVersion || '-'}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : null}
          </div>

          {/* Right Column - Diet Results (1/2) */}
          <div className="p-6 bg-white dark:bg-gray-900 overflow-y-auto">
            <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">Result</h2>


            {/* Messages - Only show when diets are not separated */}
            {(() => {
              const messages = modalData?.messages;
              const hasMessages = messages && Array.isArray(messages) && messages.length > 0;
              if (!hasMessages) return null;
              // Check if diets will be separated to determine if messages should be shown here
              const hasUserSelectedFeed = recommendation?.userSelectedFeedId > 0;
              const hasDiets = recommendation?.diets && Array.isArray(recommendation.diets) && recommendation.diets.length > 0;
              const hasCommercialFeedMessage = hasMessages && messages.some(message =>
                message?.key && typeof message.key === 'string' &&
                message.key.toLowerCase().includes('commercialfeed')
              );
              const shouldSeparateDiets = (hasUserSelectedFeed || hasCommercialFeedMessage) && hasDiets;

              if (shouldSeparateDiets) {
                // Messages will be rendered within diet sections, so don't show them here
                return null;
              }

              // Show all messages here when diets are not separated
              return (
                <div className="space-y-2 mb-4">
                  {messages.map((message, index) => (
                    <div
                      key={index}
                      className={`p-3 rounded-lg text-sm ${
                        message.type === 'ERROR'
                          ? 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800'
                          : message.type === 'WARNING'
                          ? 'bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-300 border border-yellow-200 dark:border-yellow-800'
                          : 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800'
                      }`}
                    >
                      <div
                        dangerouslySetInnerHTML={{
                          __html: message.message
                        }}
                      />
                    </div>
                  ))}
                </div>
              );
            })()}

            {/* Diet Details */}
            {(() => {
              const diets = recommendation?.diets;
              const hasDiets = diets && Array.isArray(diets) && diets.length > 0;
              if (!showEmptyData && !hasDiets) return null;
              // Check if we should separate diets based on two conditions:
              // 1. userSelectedFeedId > 0
              // 2. Any message has 'key' containing 'commercialfeed'
              const hasUserSelectedFeed = recommendation?.userSelectedFeedId > 0;
              const hasCommercialFeedMessage = recommendation?.messages && Array.isArray(recommendation.messages) &&
                recommendation.messages.some(message =>
                  message?.key && typeof message.key === 'string' &&
                  message.key.toLowerCase().includes('commercialfeed')
                );

              const shouldSeparateDiets = hasUserSelectedFeed || hasCommercialFeedMessage;

              if (!shouldSeparateDiets) {
                // Show all diets together without distinction
                return (
                  <div className="space-y-4">
                    {recommendation.diets.map((diet, index) => (
                      <div key={index} className="rounded-lg overflow-hidden">
                        <button
                          className="w-full flex items-center justify-between p-3 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors text-left"
                          onClick={() => toggleSection(`diet-${index}`)}
                        >
                          <span className="font-medium text-gray-900 dark:text-white">
                            Diet {index + 1}
                            {diet?.form && ` (${diet.form})`}
                          </span>
                          <svg
                            className={`w-4 h-4 transition-transform text-gray-600 dark:text-gray-300 ${expandedSections.has(`diet-${index}`) ? 'rotate-180' : ''}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                        {expandedSections.has(`diet-${index}`) && (
                          <div className="mt-2 p-4 space-y-4 text-sm text-gray-800 dark:text-gray-200">
                            {/* Diet Feeds Table */}
                            {diet?.feeds && Array.isArray(diet.feeds) && diet.feeds.length > 0 && (
                              <div>
                                <div className="overflow-x-auto">
                                  <table className="w-full text-xs border border-gray-200 dark:border-gray-600">
                                    <thead>
                                      <tr className="bg-gray-50 dark:bg-gray-700">
                                        <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-300 border-r border-gray-200 dark:border-gray-600">ID</th>
                                        <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-300 border-r border-gray-200 dark:border-gray-600">Feed Name</th>
                                        <th className="px-3 py-2 text-right font-medium text-gray-600 dark:text-gray-300">Amount</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {diet.feeds.map((feed, feedIndex) => (
                                        <tr key={feedIndex} className="border-t border-gray-200 dark:border-gray-600">
                                          <td className="px-3 py-2 text-gray-800 dark:text-gray-200 border-r border-gray-200 dark:border-gray-600">
                                            {feed?.id || '-'}
                                          </td>
                                          <td className="px-3 py-2 text-gray-800 dark:text-gray-200 border-r border-gray-200 dark:border-gray-600">
                                            {feed?.name || `Feed ${feedIndex + 1}`}
                                          </td>
                                          <td className="px-3 py-2 text-right text-gray-800 dark:text-gray-200">
                                            {(() => {
                                              const quantity = feed?.quantity;
                                              const unit = feed?.unit;
                                              if (quantity !== undefined && quantity !== null) {
                                                const formattedQuantity = typeof quantity === 'number' ?
                                                  Number(quantity).toFixed(2) : quantity;
                                                return unit ? `${formattedQuantity} ${unit}` : String(formattedQuantity);
                                              }
                                              return '-';
                                            })()}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                );
              }

              // Separate diets into System Feed and User Selected Feed
              const systemFeedDiets = [];
              const userSelectedFeedDiets = [];

              recommendation.diets.forEach((diet) => {
                const formName = diet?.formName || diet?.form || '';
                if (formName.toLowerCase().includes('user selected feed')) {
                  userSelectedFeedDiets.push(diet);
                } else {
                  systemFeedDiets.push(diet);
                }
              });

              // Separate messages into System Feed and Commercial Feed messages
              const systemFeedMessages = [];
              const commercialFeedMessages = [];

              if (modalData?.messages && Array.isArray(modalData.messages)) {
                modalData.messages.forEach((message) => {
                  if (message?.key && typeof message.key === 'string' &&
                      message.key.toLowerCase().includes('commercialfeed')) {
                    commercialFeedMessages.push(message);
                  } else {
                    systemFeedMessages.push(message);
                  }
                });
              }

              // Helper function to render messages
              const renderMessages = (messages) => {
                if (!messages || messages.length === 0) return null;

                return (
                  <div className="space-y-2 mb-4">
                    {messages.map((message, index) => (
                      <div
                        key={index}
                        className={`p-3 rounded-lg text-sm ${
                          message.type === 'ERROR'
                            ? 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800'
                            : message.type === 'WARNING'
                            ? 'bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-300 border border-yellow-200 dark:border-yellow-800'
                            : 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800'
                        }`}
                      >
                        <div
                          dangerouslySetInnerHTML={{
                            __html: message.message
                          }}
                        />
                      </div>
                    ))}
                  </div>
                );
              };

              const renderDietSection = (diets, sectionTitle, keyPrefix, messages) => {
                if (diets.length === 0 && (!messages || messages.length === 0)) return null;

                return (
                  <div className="mb-6">
                    <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-4">{sectionTitle}</h3>
                    {/* Render messages for this section */}
                    {renderMessages(messages)}
                    {/* Render diets for this section */}
                    <div className="space-y-4">
                      {diets.map((diet, index) => (
                        <div key={`${keyPrefix}-${index}`} className="rounded-lg overflow-hidden">
                          <button
                            className="w-full flex items-center justify-between p-3 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors text-left"
                            onClick={() => toggleSection(`${keyPrefix}-${index}`)}
                          >
                            <span className="font-medium text-gray-900 dark:text-white">
                              Diet {index + 1}
                              {diet?.form && ` (${diet.form})`}
                            </span>
                            <svg
                              className={`w-4 h-4 transition-transform text-gray-600 dark:text-gray-300 ${expandedSections.has(`${keyPrefix}-${index}`) ? 'rotate-180' : ''}`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </button>
                          {expandedSections.has(`${keyPrefix}-${index}`) && (
                            <div className="mt-2 p-4 space-y-4 text-sm text-gray-800 dark:text-gray-200">
                              {/* Diet Feeds Table */}
                              {diet?.feeds && Array.isArray(diet.feeds) && diet.feeds.length > 0 && (
                                <div>
                                  <div className="overflow-x-auto">
                                    <table className="w-full text-xs border border-gray-200 dark:border-gray-600">
                                      <thead>
                                        <tr className="bg-gray-50 dark:bg-gray-700">
                                          <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-300 border-r border-gray-200 dark:border-gray-600">ID</th>
                                          <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-300 border-r border-gray-200 dark:border-gray-600">Feed Name</th>
                                          <th className="px-3 py-2 text-right font-medium text-gray-600 dark:text-gray-300">Amount</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {diet.feeds.map((feed, feedIndex) => (
                                          <tr key={feedIndex} className="border-t border-gray-200 dark:border-gray-600">
                                            <td className="px-3 py-2 text-gray-800 dark:text-gray-200 border-r border-gray-200 dark:border-gray-600">
                                              {feed?.id || '-'}
                                            </td>
                                            <td className="px-3 py-2 text-gray-800 dark:text-gray-200 border-r border-gray-200 dark:border-gray-600">
                                              {feed?.name || `Feed ${feedIndex + 1}`}
                                            </td>
                                            <td className="px-3 py-2 text-right text-gray-800 dark:text-gray-200">
                                              {(() => {
                                                const quantity = feed?.quantity;
                                                const unit = feed?.unit;
                                                if (quantity !== undefined && quantity !== null) {
                                                  const formattedQuantity = typeof quantity === 'number' ?
                                                    Number(quantity).toFixed(2) : quantity;
                                                  return unit ? `${formattedQuantity} ${unit}` : String(formattedQuantity);
                                                }
                                                return '-';
                                              })()}
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              };

              return (
                <div>
                  {renderDietSection(systemFeedDiets, 'System Feed Diets', 'system-diet', systemFeedMessages)}
                  {renderDietSection(userSelectedFeedDiets, 'User Selected Feed Diet', 'user-diet', commercialFeedMessages)}
                </div>
              );
            })()}

            {/* Reports Section */}
            {(() => {
              const reports = modalData?.reports;
              const hasReports = reports && Array.isArray(reports) && reports.length > 0;
              if (!showEmptyData && !hasReports) return null;

              return (
                <div className="mt-6">
                  <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">Reports</h2>
                  {hasReports ? (
                    <ReportsAccordion
                      reports={reports}
                      expandedSections={expandedSections}
                      toggleSection={toggleSection}
                    />
                  ) : (
                    <NoData />
                  )}
                </div>
              );
            })()}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer className="bg-gray-50 dark:bg-gray-800 py-3">
        <div className="flex justify-center w-full">
          <button
            onClick={() => analysisUrl && window.open(analysisUrl, '_blank')}
            disabled={!analysisUrl}
            className={`px-6 py-2 rounded-lg transition-colors font-medium ${
              analysisUrl
                ? 'bg-blue-600 hover:bg-blue-700 text-white'
                : 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
            }`}
          >
            Open in Nano
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default DietRequestDetails;
